import React from "react";
import { styled } from "styled-components";
import { SchemaField } from "./configTypes";
import { describeAge, Freshness } from "./configModel";
import { tokens } from "./tokens";
import { Badge } from "../../components/ui/badge";

/**
 * How old one field's value is, said the same way wherever it is said.
 *
 * Three surfaces ask this question — the config panel on the tractor page, the
 * expanded row in the fleet table, and (by its absence) the attention count on
 * the row itself. They were answering it differently: the panel had the careful
 * version and the table had a second, blunter one written later. Two vocabularies
 * for one fact is how a reader learns to distrust both.
 *
 * The panel's version is the one that survived, because it had already worked
 * out the hard part:
 *
 * - **Stale** is a warning, and looks like one.
 * - **Unconfirmed is not.** It is the *stamp* that is missing, not the value.
 *   Rendering it as a badge beside the value produced "✓ Confirmed
 *   UNCONFIRMED" — both halves true, the pair unreadable. So it reads as a
 *   quiet note about the record instead.
 * - **An age is worth saying even when nothing is wrong**, on the fields whose
 *   whole point is recency, and in both absolute and relative terms because
 *   "5 Aug 2026" only answers the question if you know today's date.
 *
 * Tooltips differ by host rather than by choice: the panel has a Radix provider
 * and passes `hint`; the table row does not and falls back to `title`. Same
 * words either way.
 */
interface FieldFreshnessProps {
  field: SchemaField;
  fresh: Freshness;
  /** Wrap a marker in the caller's own tooltip. Defaults to a `title`. */
  hint?: (text: string, child: React.ReactNode) => React.ReactNode;
}

const FieldFreshness: React.FC<FieldFreshnessProps> = ({
  field,
  fresh,
  hint,
}) => {
  const wrap = (text: string, child: React.ReactNode) =>
    hint ? hint(text, child) : <NativeHint title={text}>{child}</NativeHint>;

  if (fresh.stale) {
    return (
      <>
        {wrap(
          `${describeAge(fresh.age)} — the threshold is ${field.stale_after_days} days.`,
          <Badge tone="warn">stale</Badge>,
        )}
      </>
    );
  }

  if (fresh.unconfirmed) {
    return (
      <>
        {wrap(
          "This value predates per-approval confirmation stamps, so there is no record of when it was last confirmed. Editing it, or re-affirming it, will start one.",
          <Age>no date on record</Age>,
        )}
      </>
    );
  }

  if (fresh.age !== null && (field.confirmable || field.type === "date")) {
    return (
      <Age>
        {field.confirmable ? "confirmed " : ""}
        {describeAge(fresh.age)}
      </Age>
    );
  }

  return null;
};

const NativeHint = styled.span`
  display: inline-flex;
  cursor: help;
`;

const Age = styled.span`
  font-size: 11.5px;
  color: ${tokens.slate};
`;

export default FieldFreshness;
